import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MagnifyingGlassIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  ClockIcon,
  ChartBarIcon,
  StarIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { Campaign, Match } from '../../types';
import { graphqlApi } from '../../services/graphql';
import { useAuthStore } from '../../store/authStore';
import { Spinner } from '../common/Spinner';
import CampaignGridCard from '../campaigns/CampaignGridCard';

export const InfluencerDashboard: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const { user } = useAuthStore();
  const [availableCampaigns, setAvailableCampaigns] = useState<Campaign[]>([]);
  const [myMatches, setMyMatches] = useState<Match[]>([]);
  const [appliedCampaigns, setAppliedCampaigns] = useState<Campaign[]>([]);
  const [influencerProfile, setInfluencerProfile] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalApplications: 0,
    acceptedMatches: 0,
    pendingMatches: 0,
    completedMatches: 0,
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (user?.userId) {
      loadInfluencerProfile();
    }
  }, [user]);

  const loadDashboardData = async () => {
    if (!user?.userId) { setIsLoading(false); return; }
    try {
      setIsLoading(true);
      const [campaignsResponse, matchesResponse] = await Promise.all([
        graphqlApi.getCampaigns(),
        graphqlApi.getMatches({ userId: user.userId }),
      ]);

      if (campaignsResponse.success && campaignsResponse.data) {
        const campaignData = Array.isArray(campaignsResponse.data) ? campaignsResponse.data : campaignsResponse.data.campaigns || [];
        setAvailableCampaigns(campaignData.filter(c => c.status === 'active').slice(0, 4));
      }

      if (matchesResponse.success && matchesResponse.data) {
        const matchData = Array.isArray(matchesResponse.data) ? matchesResponse.data : matchesResponse.data.matches || [];
        const recent = matchData.slice(0, 4);
        setMyMatches(recent);
        setStats({
          totalApplications: matchData.length,
          acceptedMatches: matchData.filter(m => m.status === 'accepted').length,
          pendingMatches: matchData.filter(m => m.status === 'applied' || m.status === 'shortlisted' || m.status === 'requested').length,
          completedMatches: matchData.filter(m => m.status === 'completed').length,
        });

        // Fetch campaign for each recent match so we can reuse CampaignGridCard.
        // Keep order aligned with `recent` — filter nulls for deleted campaigns.
        const campaigns = await Promise.all(
          recent.map(async (m) => {
            if (!m.campaignId) return null;
            try {
              const res = await graphqlApi.getCampaign(m.campaignId);
              return res.success && res.data ? res.data : null;
            } catch {
              return null;
            }
          })
        );
        setAppliedCampaigns(campaigns.filter((c): c is Campaign => !!c));
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadInfluencerProfile = async () => {
    if (!user?.userId) return;
    try {
      const response = await graphqlApi.getInfluencerByUserId(user.userId);
      if (response.success && response.data) {
        setInfluencerProfile(response.data);
      } else {
        // No profile exists yet
        setInfluencerProfile(null);
        console.warn('Influencer Profile does not exist');
      }
    } catch (error) {
      console.error('Error loading influencer profile:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Link to="/my-profile/info" className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:border-blue-300 transition-colors">
          <div className="flex items-center">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <UserCircleIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">{t('stats.profileStatus')}</h3>
              <p className="text-sm font-semibold text-indigo-600">{t('actions.updateProfile')}</p>
            </div>
          </div>
        </Link>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BriefcaseIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">{t('stats.totalApplications')}</h3>
              <p className="text-2xl font-bold text-gray-900">{stats.totalApplications}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">{t('stats.accepted')}</h3>
              <p className="text-2xl font-bold text-gray-900">{stats.acceptedMatches}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <ClockIcon className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">{t('stats.pending')}</h3>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingMatches}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">{t('quickActions')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/discovery" className="flex items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
            <MagnifyingGlassIcon className="h-6 w-6 text-blue-600 mr-3" />
            <span className="font-medium text-blue-900">{t('actions.discoverCampaigns')}</span>
          </Link>
          <Link to="/my-profile/profile" className="flex items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
            <ChartBarIcon className="h-6 w-6 text-green-600 mr-3" />
            <span className="font-medium text-green-900">{t('actions.updateProfile')}</span>
          </Link>
          <Link to="/my-profile/campaigns" className="flex items-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
            <BriefcaseIcon className="h-6 w-6 text-purple-600 mr-3" />
            <span className="font-medium text-purple-900">{t('actions.myApplications')}</span>
          </Link>
        </div>
      </div>

      {/* Available Campaigns — Grid Cards */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">{t('availableCampaigns')}</h2>
          <Link to="/discovery" className="text-blue-600 hover:text-blue-800 text-sm font-medium">{t('viewAll')}</Link>
        </div>
        {availableCampaigns.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
            <MagnifyingGlassIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">{t('noAvailableCampaigns')}</p>
            <Link to="/discovery" className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
              <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
              {t('actions.discoverCampaigns')}
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {availableCampaigns.map((campaign, idx) => (
              <CampaignGridCard key={campaign.campaignId} campaign={campaign} index={idx} linkPrefix="/campaigns" />
            ))}
          </div>
        )}
      </div>

      {/* Recent Applications — Grid Cards */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">{t('recentApplications')}</h2>
          <Link to="/my-profile/campaigns" className="text-blue-600 hover:text-blue-800 text-sm font-medium">{t('viewAll')}</Link>
        </div>
        {myMatches.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
            <BriefcaseIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">{t('noRecentApplications')}</p>
            <Link to="/discovery" className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
              <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
              {t('actions.discoverCampaigns')}
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {appliedCampaigns.map((campaign, idx) => (
              <CampaignGridCard key={campaign.campaignId} campaign={campaign} index={idx} linkPrefix="/campaigns" />
            ))}
          </div>
        )}
      </div>

      {/* Tips for Success */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">{t('tips.title')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start space-x-3">
            <div className="p-1 bg-blue-100 rounded-full"><CheckCircleIcon className="h-5 w-5 text-blue-600" /></div>
            <div><h3 className="font-medium text-gray-900">{t('tips.completeProfile.title')}</h3><p className="text-sm text-gray-600">{t('tips.completeProfile.description')}</p></div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="p-1 bg-green-100 rounded-full"><StarIcon className="h-5 w-5 text-green-600" /></div>
            <div><h3 className="font-medium text-gray-900">{t('tips.applyEarly.title')}</h3><p className="text-sm text-gray-600">{t('tips.applyEarly.description')}</p></div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="p-1 bg-purple-100 rounded-full"><ChartBarIcon className="h-5 w-5 text-purple-600" /></div>
            <div><h3 className="font-medium text-gray-900">{t('tips.beProfessional.title')}</h3><p className="text-sm text-gray-600">{t('tips.beProfessional.description')}</p></div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="p-1 bg-orange-100 rounded-full"><BriefcaseIcon className="h-5 w-5 text-orange-600" /></div>
            <div><h3 className="font-medium text-gray-900">{t('tips.deliverQuality.title')}</h3><p className="text-sm text-gray-600">{t('tips.deliverQuality.description')}</p></div>
          </div>
        </div>
      </div>
    </div>
  );
};
